// A file's diff, never blocked by its extras: when the engine fails to
// compute a file in full, the file is still shown, as plainly as it takes,
// with a note on what is missing (components/ShortfallNote.svelte).
import type { BlockSet, BlocksInput } from '../engine/types';
import { failureOf, type Failure } from '../api/failure';
import { LruMap } from '../api/cache';
import { textHash } from '../logic/textHash';
import { eng } from './engine';
import { fallbackEngine } from './fallback';

/**
 * What a file's diff lacks because the engine failed computing it in full
 * (`fileBlocks`), with that failure (an `engine_bug` or `engine_slow`) for
 * Details:
 * - `extras`: the engine's plain diff (`BlocksInput.plain`): no complexity,
 *   function names as block titles, moved-line links, similarity pairing or
 *   word highlights;
 * - `diff`: the TypeScript fallback's diff of the texts (or the patch's
 *   hunks where they describe them), rows paired by position, besides.
 *
 * `remembered`: the full computation was not tried this time, having
 * failed earlier this session (`failure`, as and when it happened).
 */
export interface Shortfall {
  level: 'extras' | 'diff';
  failure: Failure;
  remembered: boolean;
}

/**
 * For the session (one engine build): the inputs whose full computation
 * failed, by `inputKey`, with how and when (the `Failure` as it happened,
 * and the error to reject with). The same texts are not tried in full
 * again unless asked afresh: a prefetch, a reload of the comparison or the
 * other whitespace mode's lookup goes straight to the plain diff, and the
 * worker is not held again by what failed it.
 */
const fullFailed = new LruMap<string, { error: unknown; failure: Failure }>(500);

/** What a computation depends on: the path, the texts, the patch and the options. */
function inputKey(i: BlocksInput): string {
  const o = i.options;
  return [i.path, textHash(i.oldText), textHash(i.newText), i.patch === null ? '-' : textHash(i.patch), i.language ?? '-', i.recompute, o.ignoreWhitespace, o.context, o.algorithm].join('\0');
}

/**
 * A file's blocks, never failing for want of an extra: the engine's full
 * computation, within its share of the time (`FULL_BLOCKS_MS`); if that
 * fails (a panic, a trap, a time-out, an error), the engine's plain diff of
 * the same input, within the rest; if that fails too, the fallback's. Only
 * when even the fallback throws does it reject, with the engine's first
 * failure. The keys of the plain diff are the full one's, so review marks
 * hold either way. Input whose full computation failed before goes
 * straight to the plain diff, unless `fresh` (Try again).
 */
export async function fileBlocks(input: BlocksInput, { fresh = false } = {}): Promise<{ set: BlockSet; shortfall: Shortfall | null }> {
  const key = inputKey(input);
  let failed = fresh ? undefined : fullFailed.get(key);
  const remembered = failed !== undefined;
  if (!failed) {
    try {
      const set = await eng.blocks(input);
      fullFailed.delete(key);
      return { set, shortfall: null };
    } catch (e) {
      failed = { error: e, failure: failureOf(e) };
      fullFailed.set(key, failed);
      console.warn(`engine.blocks failed for ${input.path}; trying its plain diff`, e);
    }
  }
  const { error: first, failure } = failed;
  try {
    return { set: await eng.blocks({ ...input, plain: true }), shortfall: { level: 'extras', failure, remembered } };
  } catch (e) {
    console.warn(`engine.blocks failed for ${input.path} plain too; using the fallback's diff`, e);
  }
  try {
    return { set: fallbackEngine.blocks(input), shortfall: { level: 'diff', failure, remembered } };
  } catch (e) {
    console.warn(`the fallback's diff failed for ${input.path}`, e);
    throw first;
  }
}

/** What the Files view says of a shortfall: what is missing, and why, in plain words. */
export function shortfallWords(s: Shortfall): string {
  const why = s.failure.cause === 'engine_slow' ? 'working them out took too long' : 'Changeling hit a bug working them out';
  if (s.level === 'extras') return `Complexity, function names, moved-line links and word highlights aren't shown for this file: ${why}.`;
  return `This file is shown with a simpler diff, lined up by position and without complexity, function names, moved-line links or word highlights: ${why}.`;
}

/** For a remembered shortfall, that the full computation was not tried this time and when it failed; else null. */
export function rememberedWords(s: Shortfall): string | null {
  if (!s.remembered) return null;
  const when = new Date(s.failure.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `Not tried again this time: it failed at ${when}. Try again to retry it.`;
}
